/* eslint-disable @typescript-eslint/camelcase */
import { Component, Vue } from "vue-property-decorator";
import Cookies from 'js-cookie';

import { BlankPage, Loading, Nav, LBlist, LBdetail, LBtable, Hint } from "@/components";
import { Reconciliation, Common, ScheduleReport } from "@/constant";
import { AppropriationModel, ScheduleReportModel } from "@/models";
import { TCBReceiptApi } from "@/network/pb/proto";

import { UserInfo } from "@/types/Login";

import { Priv } from "@/util/Priv";
import { DefaultPath } from "@/constant/GroupPermission";
import { Util } from "@/util/Util";

// 特店 - 子項目 (每個特店資訊)
type Store = {
    id: number;
    code: string;
    name: string;
    selected: boolean;
};

// 特店 - 主項目 (統編號碼 + 全選)
type StoreGroup = {
    tidNumber: string;
    selected: boolean;
    storeList: Store[];
};

// 統計種類
type TotalItem = {
    infoType: number; // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

    batchNo: string; // 批號小結
    subCount: number; // 筆數
    subTotal: number; // 小計

    dayTotal: number; // 每日小結

    terminal: string; // 端末機號小結
    terminalCount: number; // 端末機號小結 - 筆數
    terminalTotal: number; // 端末機號小結 - 小計

    count: number; // 合計 - 筆數
    total: number; // 合計 - 總計
};

// 統計支付寶
type AlipayItem = {
    infoType: number; // 種類 [2 => 小計, 3 => 合計]

    count: number; // 小計 / 合計 - 筆數
    outAmount: number; // 小計 / 合計 - 境外交易金額
    amount: number; // 小計 / 合計 - 交易金額
};

// 統計調帳
type MotifyItem = {
    infoType: number; // 種類 [2 => 小計, 3 => 合計]

    count: number; // 小計 / 合計 - 筆數
    amount: number; // 小計 / 合計 - 調整金額
    charge: number; // 小計 / 合計 - 調整手續費
};

// 撥款彙總 查看明細
type AppropriationDetailTotal = { infoType: number; terminalNo: string; count: number; total: number; };
type AppropriationDetailInfo = (TCBReceiptApi.Protobuf.AppropriationInfoCredit | AppropriationDetailTotal)[];
type AppropriationDetail = {
    // 特店代號
    [storeCode: string]: {
        // 端末機號
        [terminalNo: string]: {
            // 撥款日
            [date: string]: {
                // 類別(    1: C信用卡與非金融卡以外的, 2: F金融卡)
                [cardType: string]: AppropriationDetailInfo
            }
        }
    }
};

@Component<ReconciliationAppropriationController>({
    components: {
        BlankPage,
        Nav,
        Loading,
        LBlist,
        LBdetail,
        LBtable,
        Hint,
    },
})
export default class ReconciliationAppropriationController extends Vue {
    private isLoading = true; // 控制顯示header

    // 搜尋引擎 - 空用元件
    // private isSearch
    private expireTime = 0;
    private periodMap = Reconciliation.PeriodMap;
    private transactionMap = Reconciliation.TransactionMap;
    private noteMap = Reconciliation.AppropriationNoteMap;
    private cardMap = Reconciliation.CardMap;
    private typeFlagMap = Reconciliation.TypeFlagMap;
    private minDate = "";
    private maxDate = "";

    private searchItem: {
        storeType: number;
        tidNumber: string;
        storeArr: string[];
        type: number; // 搜尋欄位 { 1: 撥款日區間, 2: 交易日, 3: 結帳請款日 }
        periodType: number; // 日期區間
        start: string;
        end: string;
        transactionType: number;
        noteType: number;

        cardType: string[];
        cardNum: string;
        orderNum: string;
        machineCodeStart: string;
        machineCodeEnd: string;
        authCode: string;
    } = {
            storeType: 0,
            tidNumber: "",
            storeArr: [],
            type: 1, // 前端固定是搜尋 撥款日區間 = 1
            periodType: 1,
            start: "",
            end: "",
            transactionType: 0,
            noteType: 0,
            cardType: ["1", "2", "3", "4", "5"],
            cardNum: "",
            orderNum: "",
            machineCodeStart: "",
            machineCodeEnd: "",
            authCode: "",
        };
    private datePeriodStartMax = "";

    private tidNumberArr:string[] = [];

    // 選取特約清單 => 是否展開LB
    private storeGroupList: StoreGroup[] = [];
    private isOpenStoreLB = false;

    // filter => 依特店彙總, 依入帳號彙總
    private filterType = "detail";

    // 列表
    private reconciliationList: TCBReceiptApi.Protobuf.IReconciliation[] = [];
    private appropriationMap = TCBReceiptApi.Protobuf.AppropriationMap.create();

    // 明細
    private byCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoCredit | TotalItem)[] } = {};
    private byMotifyMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoMotify | MotifyItem)[] } = {};
    private byAlipayMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoAlipay | AlipayItem)[] } = {};

    // 撥款彙總
    private byAppropriationMap: { [k: string]: TCBReceiptApi.Protobuf.AppropriationInfoSummary[] } = {};

    // 卡別彙總
    private cardTypeMap: { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryByCardType } = {};
    private creditCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] } = {};
    private debitCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] } = {};

    // 總計 - 明細 / 依撥款彙總 / 依卡別彙總
    private detailTotalObj: { count: number, total: number } = { count: 0, total: 0 };
    private appropriationTotalObj: { count: number, total: number } = { count: 0, total: 0 };
    private cardTypeTotalObj: { count: number, total: number } = { count: 0, total: 0 };

    // 展開收合
    private isUnfold = false;
    private unfoldList: string[] = [];

    // 展開明細
    private showAllList: string[] = [];

    // 查看明細 => 是否展開LB
    private isOpenDetailLB = false;
    private detailTitle: object = {};
    private detail = TCBReceiptApi.Protobuf.AppropriationInfoCredit.create();
    private detailType = 1; // 預設 1 => 請款, 2 => 撥款

    // 查看明細 => 依撥款彙總的查看
    private appropriationDetail: AppropriationDetail = {};
    private isOpenTableLB = false;
    private batchNoTitle = TCBReceiptApi.Protobuf.Reconciliation.create();
    private batchNoInfo: object = {};
    private tableType = 1; // 預設 1 => 請款, 2 => 撥款

    // LB
    private hintBox: { show: boolean, msg: string } = { show: false, msg: "" };

    // 屬於該帳號下的統編 => 用來取得特店清單
    private tidNumber = "";

    public async created() {
        /* 取得最新的權限map並判斷有何權限 */
        await this.setPriv();
        /* 取得最新的權限map並判斷有何權限 */

        const userInfo: UserInfo = JSON.parse(Cookies.get("appUserInfo") as string);
        this.tidNumber = userInfo.identification as string;
        console.log(this.tidNumber);

        const day = new Date();
        this.searchItem.end = day.toISOString().split("T")[0];
        this.datePeriodStartMax = day.toISOString().split("T")[0];
        this.searchItem.start = day.toISOString().split("T")[0];

        /* sample data */
        // 一般撥款資料
        // this.searchItem.start = "2021-03-15";
        // this.searchItem.end = "2021-04-15";
        // 支付寶用
        // this.searchItem.start = "2020-06-29";
        // this.searchItem.end = "2020-06-29";
        /* sample data */        

        this.isLoading = false;

        localStorage.removeItem("FrontReconciliationAppropriation");
    }

    /* 選取特店清單LB */
    // 取得特店清單
    private async getStoreList(tidNumber: string) {
        const item = TCBReceiptApi.Protobuf.StoreInsertQuery.create({
            type: TCBReceiptApi.Protobuf.StoreInsertQueryType.SIQT_TID_NUMBER,
            identification: tidNumber,
        });

        const storeInsertQuery = TCBReceiptApi.Protobuf.StoreInsertQuery.encode(
            item
        ).finish();
        await AppropriationModel.getStoreSearch(storeInsertQuery);

        /* 取得該帳號下的特店清單 */
        const storeUserPermissionMap = AppropriationModel.getStoreList().map;
        console.log("StoreUserPermissionMap");
        console.log(storeUserPermissionMap);

        Object.keys(storeUserPermissionMap).forEach(thisTidNumber => {
            const rawSearchList = storeUserPermissionMap[thisTidNumber] as TCBReceiptApi.Protobuf.StoreUserPermissionList;
            // 組成map傳至LB給component使用
            const storeList: Store[] = [];

            rawSearchList.list.forEach((thisStore: any) => {
                const store: Store = {
                    id: thisStore.storeId as number,
                    code: thisStore.storeCode as string,
                    name: thisStore.registerName?.trim() as string,
                    selected: true,//預設全選
                };

                storeList.push(store);
            });

            const storeGroup: StoreGroup = {
                tidNumber: thisTidNumber,
                selected: true,//預設全選
                storeList: storeList,
            };

            this.storeGroupList.push(storeGroup);
        });
        /* 取得該帳號下的特店清單 */
    }

    private closeLBlist() {
        this.isOpenStoreLB = false;
    }

    private selectStore(type: number, tidNumber: string, storeArr: string[], tidNumberArr: string[]) {
        this.searchItem.storeType = type;
        this.searchItem.tidNumber = tidNumber;
        this.searchItem.storeArr = storeArr;
        this.tidNumberArr = tidNumberArr;

        this.isOpenStoreLB = false;
    }
    /* 選取特店清單LB */

    /* 起訖日期 */
    private bindDateType() {
        if (String(this.searchItem.periodType) === "2") {
            // 調整日期的起訖
            const day = new Date();
            this.searchItem.end = day.toISOString().split("T")[0];
            day.setDate(day.getDate() - 7);
            this.searchItem.start = day.toISOString().split("T")[0];
            this.datePeriodStartMax = day.toISOString().split("T")[0];
        } else if (String(this.searchItem.periodType) === "3") {
            // 調整日期的起訖
            const day = new Date();
            this.searchItem.end = day.toISOString().split("T")[0];
            day.setDate(day.getDate() - 31);
            this.searchItem.start = day.toISOString().split("T")[0];
            this.datePeriodStartMax = day.toISOString().split("T")[0];
        } else {
            const day = new Date();
            this.datePeriodStartMax = day.toISOString().split("T")[0];
        }
    }

    private bindDatePeriod() {
        if (String(this.searchItem.periodType) === "2") {
            // 調整日期的起訖
            const day = new Date(this.searchItem.start);
            this.searchItem.start = day.toISOString().split("T")[0];
            day.setDate(day.getDate() + 7);
            this.searchItem.end = day.toISOString().split("T")[0];
        } else if (String(this.searchItem.periodType) === "3") {
            // 調整日期的起訖
            const day = new Date(this.searchItem.start);
            this.searchItem.start = day.toISOString().split("T")[0];
            day.setDate(day.getDate() + 31);
            this.searchItem.end = day.toISOString().split("T")[0];
        }

        const day = new Date(this.searchItem.start);

        if (
            new Date(this.searchItem.start).getTime() >
            new Date(this.searchItem.end).getTime()
        ) {
            this.searchItem.end = this.searchItem.start;
        }

        this.minDate = day.toISOString().split("T")[0];
        day.setDate(day.getDate() + 31);
        this.maxDate = day.toISOString().split("T")[0];

        if (
            (new Date(this.searchItem.end).getTime() - new Date(this.searchItem.start).getTime()) >= 86400 * 31 * 1000
        ) {
            this.searchItem.end = day.toISOString().split("T")[0];
        }
    }
    /* 起訖日期 */

    /* LB */
    private openHint(msg: string) {
        this.hintBox.msg = msg;
        this.hintBox.show = true;
    }
    private closeHint() {
        this.hintBox.msg = "";
        this.hintBox.show = false;
    }
    /* LB */

    /* 搜尋 */
    private validCondition(): boolean {
        // 沒有選取特店
        if (this.searchItem.storeType === 0) {
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.RequireStore]);
            return false;
        }

        if (this.searchItem.start === "" || this.searchItem.end === "") {
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.RequireDate]);
            return false;
        }

        // 無法查詢未來時間
        const startUnix = new Date(this.searchItem.start).getTime();
        const endUnix = new Date(this.searchItem.end).getTime();
        const dUnix = new Date().getTime(); // today unix
        if ((startUnix > dUnix) || (endUnix > dUnix)) {
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.DatePeriodError]);
            return false;
        }

        return true;
    }

    private async searchByCondition() {
        if (!this.validCondition()) {
            return false;
        }

        if (!Util.passReconciliationByCookie()) {
            return;
        }

        this.isLoading = true;

        /* 整理condition */
        // 選取特店
        const item = TCBReceiptApi.Protobuf.AppropriationQuery.create();
        item.storeList = this.searchItem.storeArr; // 特店清單 - 輸入"特店代號""
        item.tidNumber = this.searchItem.tidNumber;
        // 左側
        item.dateType = this.searchItem.type; // 搜尋種類 [撥款日區間, 交易日, 結帳請款日]
        item.dateStart = this.searchItem.start; // 起
        item.dateEnd = this.searchItem.end; // 訖
        item.type = this.searchItem.transactionType; // 交易別
        item.flag = this.searchItem.noteType; // 註記
        // 右側
        item.cardType = this.searchItem.cardType.join(","); // 卡別
        item.cardNo = this.searchItem.cardNum; // 卡號
        item.orderNo = this.searchItem.orderNum; // 訂單編號
        item.terminalNoStart = this.searchItem.machineCodeStart; // 端末機號區間 起
        item.terminalNoEnd = this.searchItem.machineCodeEnd; // 端末機號區間 訖
        item.authCode = this.searchItem.authCode; // 授權碼
        /* 整理condition */

        console.log("================");
        console.log("撥款搜尋參數");
        console.log(item);

        /* 寫入 local storage 方便列印 */
        const storageItem: {
            filterType: string,
            search: TCBReceiptApi.Protobuf.AppropriationQuery,
        } = {
            filterType: this.filterType,
            search: item,
        }
        localStorage.setItem("FrontReconciliationAppropriation", JSON.stringify(storageItem));
        /* 寫入 local storage 方便列印 */

        const appropriationQuery = TCBReceiptApi.Protobuf.AppropriationQuery.encode(
            item
        ).finish();

        /* init */
        this.init();
        /* init */        

        /* 取得統計 */
        // 明細
        await AppropriationModel.getAppropriationTotal(appropriationQuery);
        const appropriationTotal = AppropriationModel.getAppropriationTotalData();
        await this.countDetailTotal(appropriationTotal);

        if (this.detailTotalObj.count > process.env.VUE_APP_LIST_COUNT_LIMIT) {
            // insert schedule_report
            const reportParams = TCBReceiptApi.Protobuf.ScheduleReportInfo.create();
            reportParams.type = 2;
            reportParams.fromWhere = ScheduleReport.WhereCode.Appropriation;
            reportParams.params = JSON.stringify(item);
            reportParams.count = this.detailTotalObj.count;

            const scheduleReportInfo = TCBReceiptApi.Protobuf.ScheduleReportInfo.encode(reportParams).finish();
            await ScheduleReportModel.add(scheduleReportInfo);

            this.isLoading = false;
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.ListCountExceed]);
            return false;
        }

        // 撥款彙總
        await AppropriationModel.getAppropriationSummaryTotal(appropriationQuery);
        const appropriationSummaryTotal = AppropriationModel.getAppropriationSummaryTotalData();
        this.appropriationTotalObj = appropriationSummaryTotal;

        // 卡別彙總
        const appropriationSummaryByCardTypeTotal = await AppropriationModel.getAppropriationSummaryByCardTypeCollectionTotal(appropriationQuery);
        this.cardTypeTotalObj = appropriationSummaryByCardTypeTotal;
        /* 取得統計 */

        /* 取得列表 */
        await AppropriationModel.getReimbursement(appropriationQuery);
        const reimbursementList = AppropriationModel.getReimbursementList();
        console.log("/* 取得列表 */");
        console.log(reimbursementList);
        console.log("/* 取得列表 */");
        this.reconciliationList = reimbursementList.list;
        /* 取得列表 */

        /* 取得展開資料 - map */
        await AppropriationModel.getAppropriationMap(appropriationQuery);

        const appropriationMapData = AppropriationModel.getAppropriationMapData() as TCBReceiptApi.Protobuf.AppropriationMap;
        console.log("================");
        console.log("api => get_by_condition 明細彙總，包含撥款彙總查看的資料來源: card(明細), cardByType(彙總)");
        console.log(appropriationMapData);
        this.countTotalFromMap(appropriationMapData.map);

        // 第一次預先展開
        this.unfoldList = [];
        Object.keys(appropriationMapData.map).forEach(
            (thisStoreCode) => {
                this.unfoldList.push(thisStoreCode);
            }
        );
        /* 取得展開資料 - map */

        /* 取得撥款彙總 */
        console.log("取得撥款彙總");
        await AppropriationModel.getAppropriationSummaryMap(appropriationQuery);
        const appropriationSummaryMapData = AppropriationModel.getAppropriationSummaryMapData() as TCBReceiptApi.Protobuf.AppropriationSummaryMap;
        console.log(appropriationSummaryMapData);
        this.countTotalBySummaryMap(appropriationSummaryMapData.map);
        console.log("================");
        console.log("api => get_summary_by_condition 撥款彙總列表");
        console.log(appropriationSummaryMapData);
        console.log("----彙總後----");
        console.log(this.byAppropriationMap);
        /* 取得撥款彙總 */

        /* 取得卡別彙總 */
        await AppropriationModel.getAppropriationSummaryByCardTypeCollectionMap(appropriationQuery);
        const appropriationSummaryByCardTypeCollectionMapData = AppropriationModel.getAppropriationSummaryByCardTypeCollectionMapData() as TCBReceiptApi.Protobuf.AppropriationSummaryByCardTypeCollectionMap;
        console.log("appropriationSummaryByCardTypeCollectionMapData");
        console.log(appropriationSummaryByCardTypeCollectionMapData);
        this.countTotalByCardTypeCollectionMap(appropriationSummaryByCardTypeCollectionMapData.map);
        /* 取得卡別彙總 */

        this.isLoading = false;
    }
    /* 搜尋 */

    /* filter => 明細, 依撥款彙總, 依卡別彙總 */
    private async changeFilter(type: string) {
        this.filterType = type;

        /* 寫入 local storage 方便列印 */
        const reconciliationAppropriationJson = localStorage.getItem("FrontReconciliationAppropriation") as string;
        if (reconciliationAppropriationJson && reconciliationAppropriationJson !== "") {
            const reconciliationAppropriation = JSON.parse(reconciliationAppropriationJson) as { filterType: string, search: TCBReceiptApi.Protobuf.ReimbursementQuery };

            const storageItem: {
                filterType: string,
                search: TCBReceiptApi.Protobuf.ReimbursementQuery,
            } = {
                filterType: type,
                search: reconciliationAppropriation.search,
            }
            localStorage.setItem("FrontReconciliationAppropriation", JSON.stringify(storageItem));
        }
        /* 寫入 local storage 方便列印 */        
    }
    /* filter => 明細, 依撥款彙總, 依卡別彙總 */

    /* 展開收合 */
    private checkIsUnfold(storeCode: string) {
        let isUnfold = false;
        this.unfoldList.some((thisStoreCode) => {
            if (thisStoreCode === storeCode) {
                isUnfold = true;
                return true;
            }
        });
        return isUnfold;
    }

    private async foldAndUnfoldItem(storeCode: string) {
        const isExist = this.unfoldList.findIndex(
            (thisStoreCode) => thisStoreCode === storeCode
        );
        if (isExist >= 0) {
            // 已經展開
            this.unfoldList.splice(isExist, 1);
        } else {
            // 判斷map是否有對應資料, 有 => 直接加入展開列表, 否 => 動態拿資料回來顯示
            if (!this.byCardMap[storeCode] || !this.byMotifyMap[storeCode] || !this.byAlipayMap[storeCode]
                || !this.byAppropriationMap[storeCode]
                || !this.cardTypeMap[storeCode] || !this.creditCardMap[storeCode]) {

                /* 整理condition */
                // 選取特店
                const item = TCBReceiptApi.Protobuf.AppropriationQuery.create();
                item.storeList = this.searchItem.storeArr; // 特店清單 - 輸入"特店代號""
                item.tidNumber = this.searchItem.tidNumber;
                // 左側
                item.dateType = this.searchItem.type; // 搜尋種類 [撥款日區間, 交易日, 結帳請款日]
                item.dateStart = this.searchItem.start; // 起
                item.dateEnd = this.searchItem.end; // 訖
                item.type = this.searchItem.transactionType; // 交易別
                item.flag = this.searchItem.noteType; // 註記
                // 右側
                item.cardType = this.searchItem.cardType.join(","); // 卡別
                item.cardNo = this.searchItem.cardNum; // 卡號
                item.orderNo = this.searchItem.orderNum; // 訂單編號
                item.terminalNoStart = this.searchItem.machineCodeStart; // 端末機號區間 起
                item.terminalNoEnd = this.searchItem.machineCodeEnd; // 端末機號區間 訖
                item.specificStore = storeCode; // 單一特店
                /* 整理condition */

                const appropriationQuery = TCBReceiptApi.Protobuf.AppropriationQuery.encode(
                    item
                ).finish();

                /* 取得展開資料 */
                // 明細
                await AppropriationModel.getAppropriation(appropriationQuery);
                const appropriation = AppropriationModel.getAppropriationData();
                console.log("================");
                console.log('取得展開資料 - 明細');
                console.log(appropriation);
                const detailItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriation } = {};
                detailItem[storeCode] = appropriation;
                this.countTotalFromMap(detailItem);

                // 撥款彙總
                await AppropriationModel.getAppropriationSummary(appropriationQuery);
                const appropriationSummary = AppropriationModel.getAppropriationSummaryData();
                console.log("================");
                console.log('取得展開資料 - 撥款彙總');
                console.log(appropriationSummary);
                const appropriationItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryTerminalNoMap } = {};
                appropriationItem[storeCode] = appropriationSummary;
                this.countTotalBySummaryMap(appropriationItem);

                // 卡別彙總
                await AppropriationModel.getAppropriationSummaryByCardTypeCollection(appropriationQuery);
                const appropriationSummaryByCardTypeCollection = AppropriationModel.getAppropriationSummaryByCardTypeCollectionData();
                console.log("================");
                console.log('取得展開資料 - 卡別彙總');
                console.log(appropriationSummaryByCardTypeCollection);
                const cardItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryByCardTypeCollection } = {};
                cardItem[storeCode] = appropriationSummaryByCardTypeCollection;
                this.countTotalByCardTypeCollectionMap(cardItem);
                /* 取得展開資料 */
            }
            this.unfoldList.push(storeCode);
        }
    }
    /* 展開收合 */

    /* 展開明細 */
    private showAll(storeCode: string) {
        const isExist = this.showAllList.findIndex(
            (thisStoreCode) => thisStoreCode === storeCode
        );

        if (isExist >= 0) {
            this.showAllList.splice(isExist, 1);
        } else {
            this.showAllList.push(storeCode);
        }
    }

    private IsShowAllItem(storeCode: string) {
        const isExist = this.showAllList.findIndex(
            (thisStoreCode) => thisStoreCode === storeCode
        );

        return isExist >= 0;
    }
    /* 展開明細 */

    /* 查看批號明細 */
    private openTableLB(reconciliation: TCBReceiptApi.Protobuf.Reconciliation, summaryInfo: TCBReceiptApi.Protobuf.AppropriationInfoSummary) { 
        // reconciliation 列表資訊(顯示title), summaryInfo 彙總資訊(索引map用)
        console.log("================");
        console.log('查看撥款彙總> 導入的特店');
        console.log(reconciliation);
        console.log(summaryInfo);
        // 撥款的batchNo是類別註記typeFlag
        this.tableType = 2; // 查看樣式 預設 1 => 請款, 2 => 撥款
        this.batchNoTitle = reconciliation as TCBReceiptApi.Protobuf.Reconciliation;
        this.batchNoInfo = this.appropriationDetail[reconciliation.storeCode][summaryInfo.terminalNo][summaryInfo.date][summaryInfo.typeFlag] as AppropriationDetailInfo;

        this.isOpenTableLB = true;
    }

    private closeLBtable() {
        this.isOpenTableLB = false;
    }
    /* 查看批號明細 */

    /* 查看明細 */
    private openDetailLB(
        reconciliation: any,
        info: TCBReceiptApi.Protobuf.AppropriationInfoCredit,
        type = 1 // 預設 1 => 請款, 2 => 撥款
    ) {
        this.detailTitle = reconciliation;
        this.detail = info;
        this.detailType = type;

        this.isOpenDetailLB = true;
    }

    private closeLBdetail() {
        this.isOpenDetailLB = false;
    }
    /* 查看明細 */

    /* 其他 */
    private isNotNatureNum(val: string) {
        if (val) {
            const num = (val + "").replace(/,/g, "");
            return Number(num) <= 0;
        } else {
            return false;
        }
    }

    // 數字轉千分位 (number to string) ps.保留數字小數點至第二位
    private convertNumberToMoney(num: number) {
        if (!num) {
            return 0;
        }
        let a: string = (num + "").replace(/,/g, "");
        if (a.indexOf(".") != -1) {
            if (a.split(".")[1].length > 2) {
                a = Math.round(+a * 100) / 100 + "";
            }
            if (a.split(".").length == 2) {
                return (+a.split(".")[0]).toLocaleString() + "." + a.split(".")[1];
            } else {
                return (+a.split(".")[0]).toLocaleString();
            }
        } else {
            return (+a.split(".")[0]).toLocaleString();
        }
    }

    // 統計 - 明細
    private countTotalFromMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriation; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 卡別 */
                const cardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoCredit | TotalItem)[] = []; // 紀錄卡別明細 => 每一條資料
                // 合計
                let countByCard = 0;
                let totalByCard = 0;

                const cardMenu = storeCodeMap[thisStoreCode].card as TCBReceiptApi.Protobuf.AppropriationCreditFirstMap;
                const terminalMap = cardMenu.byFirstColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditSecondMap };
                if (cardMenu && terminalMap) {
                    Object.keys(terminalMap).forEach(thisTerminal => {
                        // 端末機號小結
                        let terminalCount = 0;
                        let terminalTotal = 0;

                        const dateMap = terminalMap[thisTerminal].bySecondColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditBatchNoMap };
                        if (dateMap) {
                            Object.keys(dateMap).forEach(thisDate => {
                                // 每日小結
                                let dayTotal = 0;

                                const batchNoMap = dateMap[thisDate].byBatchNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCredit };
                                if (batchNoMap) {
                                    Object.keys(batchNoMap).forEach(thisBatchNo => {
                                        const infoList = batchNoMap[thisBatchNo].list as TCBReceiptApi.Protobuf.AppropriationInfoCredit[];
                                        if (infoList) {
                                            // 批號小結
                                            const batchNoCount = infoList.length;
                                            let batchNoTotal = 0;

                                            infoList.forEach(thisInfo => {
                                                // 每一條明細
                                                cardInfoList.push(thisInfo);
                                                // 統計 - 批號小結
                                                batchNoTotal += (thisInfo.amount as number);
                                                // 統計 - 每日小結
                                                dayTotal += (thisInfo.amount as number);
                                                // 統計 - 端末機號小結
                                                terminalCount += 1;
                                                terminalTotal += (thisInfo.amount as number);
                                                // 統計 - 合計
                                                countByCard += 1;
                                                totalByCard += (thisInfo.amount as number);
                                            });

                                            const addItem: TotalItem = {
                                                infoType: 2, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

                                                batchNo: thisBatchNo, // 批號小結
                                                subCount: batchNoCount, // 筆數
                                                subTotal: batchNoTotal, // 小計

                                                dayTotal: 0, // 每日小結

                                                terminal: "", // 端末機號小結
                                                terminalCount: 0, // 端末機號小結 - 筆數
                                                terminalTotal: 0, // 端末機號小結 - 小計

                                                count: 0, // 合計 - 筆數
                                                total: 0, // 合計 - 總計
                                            };
                                            cardInfoList.push(addItem);
                                        }
                                    });
                                }

                                // 觀念 : 為了要讓每日小結與批號小結同層 在此無法使用push新增 而是對當前cardInfoList列表最後一筆做編輯 額外設定每日小結
                                // 實作 : 直接使用arr.pop() 取得最後一組item且原先arr會被移除 再插入新整理好的item
                                const lastItem: TotalItem = cardInfoList.pop() as TotalItem;
                                if (lastItem) {
                                    const editItem: TotalItem = {
                                        infoType: 2, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

                                        batchNo: lastItem.batchNo, // 批號小結
                                        subCount: lastItem.subCount, // 筆數
                                        subTotal: lastItem.subTotal, // 小計

                                        dayTotal: dayTotal, // 每日小結

                                        terminal: "", // 端末機號小結
                                        terminalCount: lastItem.terminalCount, // 端末機號小結 - 筆數
                                        terminalTotal: lastItem.terminalTotal, // 端末機號小結 - 小計

                                        count: lastItem.count, // 合計 - 筆數
                                        total: lastItem.total, // 合計 - 總計
                                    };
                                    cardInfoList.push(editItem);
                                }
                            });
                        }

                        const addItem: TotalItem = {
                            infoType: 3, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

                            batchNo: "", // 批號小結
                            subCount: 0, // 筆數
                            subTotal: 0, // 小計

                            dayTotal: 0, // 每日小結

                            terminal: thisTerminal, // 端末機號小結
                            terminalCount: terminalCount, // 端末機號小結 - 筆數
                            terminalTotal: terminalTotal, // 端末機號小結 - 小計

                            count: 0, // 合計 - 筆數
                            total: 0, // 合計 - 總計
                        };
                        cardInfoList.push(addItem);
                    });

                    // if (countByCard > 0) {
                    const addItem: TotalItem = {
                        infoType: 4, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

                        batchNo: "", // 批號小結
                        subCount: 0, // 筆數
                        subTotal: 0, // 小計

                        dayTotal: 0, // 每日小結

                        terminal: "", // 端末機號小結
                        terminalCount: 0, // 端末機號小結 - 筆數
                        terminalTotal: 0, // 端末機號小結 - 小計

                        count: countByCard, // 合計 - 筆數
                        total: totalByCard, // 合計 - 總計
                    };
                    cardInfoList.push(addItem);
                    // }
                }

                // 裝入卡別的map
                if (cardInfoList.length > 0) {
                    this.byCardMap[thisStoreCode] = cardInfoList; // 吃嚴謹的型別 => byCardMap也需要把TotalItem加入型別宣告
                    // this.$set(this.byCardMap, thisStoreCode, cardInfoList); // 就算型別不一致也能set在同一組object, 沒有嚴謹的規範
                }
                /* 卡別 */

                /* 支付寶 */
                const alipayInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoAlipay | AlipayItem)[] = []; // 紀錄支付寶明細 => 每一條資料

                const alipayMenu = storeCodeMap[thisStoreCode].alipay as TCBReceiptApi.Protobuf.AppropriationAlipayDateMap;
                if (alipayMenu && alipayMenu.byDate) {
                    const dateMap = alipayMenu.byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationAlipay };
                    if (dateMap) {
                        // let
                        let totalCount = 0;
                        let totalAbroadAmount = 0;
                        let totalAmount = 0;

                        Object.keys(dateMap).forEach(thisDate => {
                            const infoList = dateMap[thisDate].list as TCBReceiptApi.Protobuf.AppropriationInfoAlipay[];
                            if (infoList) {
                                const dateCount = infoList.length;
                                let dateAbroadAmount = 0;
                                let dateAmount = 0;

                                infoList.forEach(thisInfo => {
                                    // 每一條明細
                                    alipayInfoList.push(thisInfo);
                                    // 統計 - 每日小計
                                    dateAbroadAmount += (thisInfo.abroadAmount as number);
                                    dateAmount += (thisInfo.amount as number);
                                    // 統計 - 合計
                                    totalCount += 1;
                                    totalAbroadAmount += (thisInfo.abroadAmount as number);
                                    totalAmount += (thisInfo.amount as number);
                                });

                                const addItem: AlipayItem = {
                                    infoType: 2, // 種類 [2 => 小計, 3 => 合計]

                                    count: dateCount, // 筆數
                                    outAmount: dateAbroadAmount, // 境外交易金額
                                    amount: dateAmount, // 交易金額
                                };
                                alipayInfoList.push(addItem);
                            }
                        });

                        if (totalCount > 0) {
                            const addItem: AlipayItem = {
                                infoType: 3, // 種類 [2 => 小計, 3 => 合計]

                                count: totalCount, // 筆數
                                outAmount: totalAbroadAmount, // 境外交易金額
                                amount: totalAmount, // 交易金額
                            };
                            alipayInfoList.push(addItem);
                        }

                    }

                }
                // 裝入調帳的map
                if (alipayInfoList.length > 0) {
                    this.byAlipayMap[thisStoreCode] = alipayInfoList; // 吃嚴謹的型別 => byAlipayMap也需要把AlipayItem加入型別宣告
                }
                /* 支付寶 */

                /* 調帳 */
                const motifyInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoMotify | MotifyItem)[] = []; // 紀錄調帳明細 => 每一條資料

                const motifyMenu = storeCodeMap[thisStoreCode].motify as TCBReceiptApi.Protobuf.AppropriationMotifyDateMap;
                if (motifyMenu && motifyMenu.byDate) {
                    const dateMap = motifyMenu.byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationMotify };
                    if (dateMap) {
                        // let
                        let totalCount = 0;
                        let totalAmount = 0;
                        let totalCharge = 0;

                        Object.keys(dateMap).forEach(thisDate => {
                            // console.log('thisDate');
                            // console.log(thisDate);

                            const infoList = dateMap[thisDate].list as TCBReceiptApi.Protobuf.AppropriationInfoMotify[];
                            if (infoList) {
                                const dateCount = infoList.length;
                                let dateAmount = 0;
                                let dateCharge = 0;

                                infoList.forEach(thisInfo => {
                                    // console.log('thisInfo');
                                    console.log(thisInfo);


                                    // 每一條明細
                                    motifyInfoList.push(thisInfo);
                                    // 統計 - 每日小計
                                    dateAmount += (thisInfo.motifyAmount as number);
                                    dateCharge += (thisInfo.motifyCharge as number);
                                    // 統計 - 合計
                                    totalCount += 1;
                                    totalAmount += (thisInfo.motifyAmount as number);
                                    totalCharge += (thisInfo.motifyCharge as number);
                                });

                                const addItem: MotifyItem = {
                                    infoType: 2, // 種類 [2 => 小計, 3 => 合計]

                                    count: dateCount, // 筆數
                                    amount: dateAmount, // 調整金額
                                    charge: dateCharge, // 調整手續費
                                };
                                motifyInfoList.push(addItem);
                            }
                        });

                        if (totalCount > 0) {
                            const addItem: MotifyItem = {
                                infoType: 3, // 種類 [2 => 小計, 3 => 合計]

                                count: totalCount, // 筆數
                                amount: totalAmount, // 調整金額
                                charge: totalCharge, // 調整手續費
                            };
                            motifyInfoList.push(addItem);
                        }

                    }

                }
                // 裝入調帳的map
                if (motifyInfoList.length > 0) {
                    this.byMotifyMap[thisStoreCode] = motifyInfoList; // 吃嚴謹的型別 => byMotifyMap也需要把MotifyItem加入型別宣告
                }
                /* 調帳 */

                /* 製作 - 撥款彙總的查看map */
                const cardTypeMenu = storeCodeMap[thisStoreCode].cardByType as TCBReceiptApi.Protobuf.AppropriationCreditFirstMap;
                const terminalMapByAppropriation = cardTypeMenu.byFirstColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditSecondMap };
                if (cardTypeMenu && terminalMapByAppropriation) {
                    Object.keys(terminalMapByAppropriation).forEach(thisTerminal => {
                        const dateMap = terminalMapByAppropriation[thisTerminal].bySecondColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditBatchNoMap };
                        if (dateMap) {
                            Object.keys(dateMap).forEach(thisDate => {
                                const batchNoMap = dateMap[thisDate].byBatchNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCredit }; // 這邊的byBatchNo是類別註記
                                if (batchNoMap) {
                                    Object.keys(batchNoMap).forEach(thisBatchNo => {
                                        console.log(`thisBatchNo = ${thisBatchNo}`);
                                        const cardTypeList: AppropriationDetailInfo = []; // 紀錄同個類別註記下的明細 => 每一條資料

                                        const infoList = batchNoMap[thisBatchNo].list as TCBReceiptApi.Protobuf.AppropriationInfoCredit[];
                                        if (infoList) {
                                            // 類別註記
                                            const batchNoCount = infoList.length;
                                            let batchNoTotal = 0;

                                            infoList.forEach(thisInfo => {
                                                // 每一條明細
                                                cardTypeList.push(thisInfo);
                                                // 統計 - 類別註記
                                                batchNoTotal += (thisInfo.amount as number);
                                            });

                                            if (cardTypeList.length > 0) {
                                                const addItem: AppropriationDetailTotal = {
                                                    infoType: 2, // [1 => 一般資料, 2 => 小計]
                                                    terminalNo: thisTerminal,
                                                    count: batchNoCount,
                                                    total: batchNoTotal,
                                                };
                                                cardTypeList.push(addItem);

                                                if (!this.appropriationDetail[thisStoreCode]) {
                                                    this.appropriationDetail[thisStoreCode] = {};
                                                }
                                                if (!this.appropriationDetail[thisStoreCode][thisTerminal]) {
                                                    this.appropriationDetail[thisStoreCode][thisTerminal] = {};
                                                }
                                                if (!this.appropriationDetail[thisStoreCode][thisTerminal][thisDate]) {
                                                    this.appropriationDetail[thisStoreCode][thisTerminal][thisDate] = {};
                                                }

                                                this.appropriationDetail[thisStoreCode][thisTerminal][thisDate][thisBatchNo] = cardTypeList;
                                            }
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
                /* 製作 - 撥款彙總的查看map */
            });

            console.log("================");
            console.log("appropriationDetail 撥款彙總> 查看的map");
            console.log(this.appropriationDetail);
        }
    }

    // 統計 - 撥款彙總
    private countTotalBySummaryMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryTerminalNoMap; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 撥款 */
                const appropriationInfoList: TCBReceiptApi.Protobuf.AppropriationInfoSummary[] = []; // 紀錄撥款明細 => 每一條資料
                // 合計
                const addTotalItem = TCBReceiptApi.Protobuf.AppropriationInfoSummary.create({ infoType: 3 });

                const terminalMap = storeCodeMap[thisStoreCode].byTerminalNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryDateMap };
                if (terminalMap) {
                    Object.keys(terminalMap).forEach(thisTerminal => {
                        const dateMap = terminalMap[thisTerminal].byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryTypeFlagMap };
                        if (dateMap) {
                            Object.keys(dateMap).forEach(thisDate => {
                                const cardTypeMenu = dateMap[thisDate].byTypeFlag as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummary };
                                if (cardTypeMenu) {
                                    Object.keys(cardTypeMenu).forEach(thisCardType => {
                                        const infoList = cardTypeMenu[thisCardType].list as TCBReceiptApi.Protobuf.AppropriationInfoSummary[];
                                        if (infoList) {
                                            const addItem = TCBReceiptApi.Protobuf.AppropriationInfoSummary.create({ infoType: 2 });

                                            infoList.forEach(thisInfo => {
                                                // 每一條明細
                                                appropriationInfoList.push(thisInfo);
                                                // 小計
                                                addItem.totalCount += (thisInfo.totalCount as number);
                                                addItem.totalAmount += (thisInfo.totalAmount as number);
                                                addItem.salesCount += (thisInfo.salesCount as number);
                                                addItem.salesAmount += (thisInfo.salesAmount as number);
                                                addItem.returnCount += (thisInfo.returnCount as number);
                                                addItem.returnAmount += (thisInfo.returnAmount as number);
                                                addItem.charge += (thisInfo.charge as number);
                                                addItem.ifr += (thisInfo.ifr as number);
                                                addItem.motifyAmount += (thisInfo.motifyAmount as number);
                                                addItem.motifyCharge += (thisInfo.motifyCharge as number);
                                                addItem.installRate += (thisInfo.installRate as number);
                                                addItem.chargeRate += (thisInfo.chargeRate as number);
                                                addItem.netAmount += (thisInfo.netAmount as number);
                                                // 合計
                                                addTotalItem.totalCount += (thisInfo.totalCount as number);
                                                addTotalItem.totalAmount += (thisInfo.totalAmount as number);
                                                addTotalItem.salesCount += (thisInfo.salesCount as number);
                                                addTotalItem.salesAmount += (thisInfo.salesAmount as number);
                                                addTotalItem.returnCount += (thisInfo.returnCount as number);
                                                addTotalItem.returnAmount += (thisInfo.returnAmount as number);
                                                addTotalItem.charge += (thisInfo.charge as number);
                                                addTotalItem.ifr += (thisInfo.ifr as number);
                                                addTotalItem.motifyAmount += (thisInfo.motifyAmount as number);
                                                addTotalItem.motifyCharge += (thisInfo.motifyCharge as number);
                                                addTotalItem.installRate += (thisInfo.installRate as number);
                                                addTotalItem.chargeRate += (thisInfo.chargeRate as number);
                                                addTotalItem.netAmount += (thisInfo.netAmount as number);
                                            });

                                            appropriationInfoList.push(addItem); // 小計
                                        }
                                    });
                                }
                            });
                        }
                    });
                }

                // 合計 並 裝上map
                // if (appropriationInfoList.length > 0) {
                appropriationInfoList.push(addTotalItem); // 合計

                // 裝入撥款的map
                this.byAppropriationMap[thisStoreCode] = appropriationInfoList; // 吃嚴謹的型別
                // }
            });
        }
    }

    // 統計 - 卡別彙總
    private countTotalByCardTypeCollectionMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryByCardTypeCollection; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 卡別彙整 */
                const cardMenu = storeCodeMap[thisStoreCode].cardType as TCBReceiptApi.Protobuf.AppropriationSummaryByCardType;
                if (cardMenu) {
                    const visa = cardMenu.visa as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const mc = cardMenu.mc as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const jcb = cardMenu.jcb as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const cup = cardMenu.cup as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const fisc = cardMenu.fisc as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const motify = cardMenu.motify as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;

                    // 合計 = VISA + MasterCard + JCB + 銀聯卡 + 金融卡 + 調帳
                    const totalByCard = TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType.create({
                        ourBankCount: visa.ourBankCount + mc.ourBankCount + jcb.ourBankCount + cup.ourBankCount + fisc.ourBankCount + motify.ourBankCount,
                        ourBankAmount: visa.ourBankAmount + mc.ourBankAmount + jcb.ourBankAmount + cup.ourBankAmount + fisc.ourBankAmount + motify.ourBankAmount,
                        notOurBankCount: visa.notOurBankCount + mc.notOurBankCount + jcb.notOurBankCount + cup.notOurBankCount + fisc.notOurBankCount + motify.notOurBankCount,
                        notOurBankAmount: visa.notOurBankAmount + mc.notOurBankAmount + jcb.notOurBankAmount + cup.notOurBankAmount + fisc.notOurBankAmount + motify.notOurBankAmount,
                        totalCount: visa.totalCount + mc.totalCount + jcb.totalCount + cup.totalCount + fisc.totalCount + motify.totalCount,
                        totalAmount: visa.totalAmount + mc.totalAmount + jcb.totalAmount + cup.totalAmount + fisc.totalAmount + motify.totalAmount,
                    });
                    this.$set(cardMenu, "total", totalByCard);

                    this.cardTypeMap[thisStoreCode] = cardMenu;
                }

                /* 卡別彙整 */

                /* 發卡行交易統計-信用卡/銀聯卡 */
                const creditCardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] = []; // 紀錄信用卡/銀聯卡明細 => 每一條資料

                const creditCardMenu = storeCodeMap[thisStoreCode].creditCard as TCBReceiptApi.Protobuf.AppropriationSummaryByIssuer;
                if (creditCardMenu) {
                    const infoList = creditCardMenu.list as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer[];
                    if (infoList) {
                        // 合計
                        let count = 0;
                        let amount = 0;

                        infoList.forEach(thisInfo => {
                            // 每一條明細
                            creditCardInfoList.push(thisInfo);
                            // 合計
                            count += (thisInfo.count as number);
                            amount += (thisInfo.amount as number);
                        });

                        const addInfo: { count: number, amount: number, infoType: number } = {
                            count: count,
                            amount: amount,
                            infoType: 2, // 註記
                        };
                        creditCardInfoList.push(addInfo);

                        if (count > 0) {
                            this.creditCardMap[thisStoreCode] = creditCardInfoList;
                        }
                    }
                }
                /* 發卡行交易統計-信用卡/銀聯卡 */

                /* 發卡行交易統計-金融卡 */
                const debitCardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] = []; // 紀錄金融卡明細 => 每一條資料

                const debitCardMenu = storeCodeMap[thisStoreCode].debitCard as TCBReceiptApi.Protobuf.AppropriationSummaryByIssuer;
                if (debitCardMenu) {
                    const infoList = debitCardMenu.list as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer[];
                    if (infoList) {
                        // 合計
                        let count = 0;
                        let amount = 0;

                        infoList.forEach(thisInfo => {
                            // 每一條明細
                            debitCardInfoList.push(thisInfo);
                            // 合計
                            count += (thisInfo.count as number);
                            amount += (thisInfo.amount as number);
                        });

                        const addInfo: { count: number, amount: number, infoType: number } = {
                            count: count,
                            amount: amount,
                            infoType: 2, // 註記
                        };
                        debitCardInfoList.push(addInfo);

                        if (count > 0) {
                            this.debitCardMap[thisStoreCode] = debitCardInfoList;
                        }
                    }
                }
                /* 發卡行交易統計-金融卡 */
            });
        }
    }

    // 統計 明細 總計 (把明細 + 支付寶 + 調帳)
    private async countDetailTotal(map: TCBReceiptApi.Protobuf.AppropriationTotal) {
        let count = 0;
        let total = 0;

        // 明細
        if (map.card) {
            if (map.card.count) {
                count += map.card.count;
            }
            if (map.card.total) {
                total += map.card.total;
            }
        }
        // 支付寶
        if (map.alipay) {
            if (map.alipay.count) {
                count += map.alipay.count;
            }
            if (map.alipay.total) {
                total += map.alipay.total;
            }
        }
        // 調帳
        if (map.motify) {
            if (map.motify.count) {
                count += map.motify.count;
            }
            if (map.motify.total) {
                total += map.motify.total;
            }
        }

        this.detailTotalObj.count = count;
        this.detailTotalObj.total = total;
    }
    /* 其他 */

    /* 特店清單LB */
    private openStoreLightbox() {
        this.isOpenStoreLB = true;
        this.$emit("searchStore", this.tidNumber);
    }
    /* 特店清單LB */

    /* 取得最新的權限map並判斷有何權限 */
    private async setPriv() {
        const meta = this.$route.meta as { privKey: { controller: string, method: string, type: string } };

        const isSetPriv = await Priv._checkPriv(meta.privKey);
        if (!isSetPriv) {
            this.$router.push(DefaultPath);  // priv
        }

        if (!Priv._checkPrivType(meta.privKey.type)) {
            this.$router.push(DefaultPath);  // priv
        }

        this.expireTime = Priv._getExpireTime();
    }
    /* 取得最新的權限map並判斷有何權限 */

    private async exportFile() {
        if (!this.validCondition()) {
            return false;
        }

        if (!Util.passReconciliationByCookie()) {
            return;
        }
        
        this.isLoading = true;

        /* 報表專區 => 追加總計判斷 */
        if (this.filterType === 'detail') {
            // 整理input (有兩處使用item整理 => 匯出也需要整理條件)
            // 選取特店
            const item = TCBReceiptApi.Protobuf.AppropriationQuery.create();
            item.storeList = this.searchItem.storeArr; // 特店清單 - 輸入"特店代號""
            item.tidNumber = this.searchItem.tidNumber;
            // 左側
            item.dateType = this.searchItem.type; // 搜尋種類 [撥款日區間, 交易日, 結帳請款日]
            item.dateStart = this.searchItem.start; // 起
            item.dateEnd = this.searchItem.end; // 訖
            item.type = this.searchItem.transactionType; // 交易別 => 1:購貨, 2:退貨
            item.flag = this.searchItem.noteType; // 註記
            // 右側
            item.cardType = this.searchItem.cardType.join(","); // 卡別
            item.cardNo = this.searchItem.cardNum; // 卡號
            item.orderNo = this.searchItem.orderNum; // 訂單編號
            item.terminalNoStart = this.searchItem.machineCodeStart; // 端末機號區間 起
            item.terminalNoEnd = this.searchItem.machineCodeEnd; // 端末機號區間 訖
            item.authCode = this.searchItem.authCode; // 授權碼

            console.log("================");
            console.log("撥款搜尋參數");
            console.log(item);

            const appropriationQuery = TCBReceiptApi.Protobuf.AppropriationQuery.encode(item).finish();

            // 明細
            await AppropriationModel.getAppropriationTotal(appropriationQuery);
            const appropriationTotal = AppropriationModel.getAppropriationTotalData();
            await this.countDetailTotal(appropriationTotal);

            if (this.detailTotalObj.count > process.env.VUE_APP_EXCEL_COUNT_LIMIT) { //
                // insert schedule_report
                const reportParams = TCBReceiptApi.Protobuf.ScheduleReportInfo.create();
                reportParams.type = 2;
                reportParams.fromWhere = ScheduleReport.WhereCode.Appropriation;
                reportParams.params = JSON.stringify(item);
                reportParams.count = this.detailTotalObj.count;

                const scheduleReportInfo = TCBReceiptApi.Protobuf.ScheduleReportInfo.encode(reportParams).finish();
                await ScheduleReportModel.add(scheduleReportInfo);

                this.isLoading = false;
                this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.ExcelCountExceed]); //
                return false;
            }
        }
        /* 報表專區 => 追加總計判斷 */          

        // 整理condition (AppropriationQuery)
        const queryArray: string[] = [];
        // 選取特店
        const storeArr: string[] = [];
        let idx = 0;

        for(let i=0; i<this.searchItem.storeArr.length; i++)
        {
            const storecodeTxt = this.searchItem.storeArr[i].substring(3, 11);

            idx = this.tidNumberArr.indexOf(storecodeTxt);
            if(idx == -1){
                storeArr.push(this.searchItem.storeArr[i]);
            }
        }

        const storeList: string = (storeArr.length !== 0) ? ("storeList=" + JSON.stringify(storeArr)) : "storeList=";
        queryArray.push(storeList);
        const tidNumber: string = (this.tidNumberArr.length !== 0) ? ("tidNumberList=" + JSON.stringify(this.tidNumberArr)) : "tidNumberList=";
        queryArray.push(tidNumber);

        if(storeArr.length > 50){
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.ExportNumExceedLimit]);
            this.isLoading = false;
            return false;
        }
        
        // 左側
        const dateType: string = (this.searchItem.type !== 0) ? ("dateType=" + this.searchItem.type) : "dateType=";
        queryArray.push(dateType);
        const dateStart: string = (this.searchItem.start !== "") ? ("dateStart=" + this.searchItem.start) : "dateStart=";
        queryArray.push(dateStart);
        const dateEnd: string = (this.searchItem.end !== "") ? ("dateEnd=" + this.searchItem.end) : "dateEnd=";
        queryArray.push(dateEnd);
        const type: string = (this.searchItem.transactionType !== 0) ? ("type=" + this.searchItem.transactionType) : "type=";
        queryArray.push(type);
        const flag: string = (this.searchItem.noteType !== 0) ? ("flag=" + this.searchItem.noteType) : "flag=";
        queryArray.push(flag);
        // 右側
        const cardType: string = (this.searchItem.cardType.length !== 0) ? ("cardType=" + this.searchItem.cardType.join(",")) : "cardType=";
        queryArray.push(cardType);
        const cardNo: string = (this.searchItem.cardNum !== "") ? ("cardNo=" + this.searchItem.cardNum) : "cardNo=";
        queryArray.push(cardNo);
        const orderNo: string = (this.searchItem.orderNum !== "") ? ("orderNo=" + this.searchItem.orderNum) : "orderNo=";
        queryArray.push(orderNo);
        const terminalNoStart: string = (this.searchItem.machineCodeStart !== "") ? ("terminalNoStart=" + this.searchItem.machineCodeStart) : "terminalNoStart=";
        queryArray.push(terminalNoStart);
        const terminalNoEnd: string = (this.searchItem.machineCodeEnd !== "") ? ("terminalNoEnd=" + this.searchItem.machineCodeEnd) : "terminalNoEnd=";
        queryArray.push(terminalNoEnd);
        const authCode: string = (this.searchItem.authCode !== "") ? ("authCode=" + this.searchItem.authCode) : "authCode=";
        queryArray.push(authCode);
        // tabType: { 1: "明細", 2: "撥款彙總", 3: "卡別彙總"}
        let tab = 0;
        switch (this.filterType) {
            case "detail":
                tab = 1;
                break;
            case "appropriation":
                tab = 2;
                break;
            case "cardType":
                tab = 3;
                break;
        }
        const tabType: string = (tab !== 0) ? ("tabType=" + tab) : "tabType=";
        queryArray.push(tabType);

        console.log("queryArray");
        console.log(queryArray);
        const queryString = (queryArray.length === 0) ? "" : queryArray.join("&") as string;
        console.log("queryString");
        console.log(queryString);

        await AppropriationModel.exportAppropriation(queryString);

        this.isLoading = false;
    }

    private init() {
        this.byCardMap = {};
        this.byAlipayMap = {};
        this.byMotifyMap = {};
        this.appropriationDetail = {};
        this.byAppropriationMap = {};
        this.cardTypeMap = {};
        this.creditCardMap = {};
        this.debitCardMap = {};
    }    
}
